'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Workspace } from '@/api/endpoints/workspaces';

// SECURITY: `currentWorkspace` is server-authoritative — populated by the
// canonical `useWorkspaces` query after auth and explicitly set via
// `setCurrentWorkspace`. It is deliberately EXCLUDED from `partialize` below
// so it does not survive a page reload via localStorage. On boot the store
// is always `currentWorkspace: null`; the layout's `useWorkspaces` query
// re-validates membership against the server and calls
// `setCurrentWorkspace()` with a fresh, server-confirmed value. Persisting
// the workspace blob would risk granting access (role, plan, name) based on
// stale localStorage if the user was demoted, removed, or moved to a
// different plan tier between sessions.
//
// `selectedDocId`/`selectedNoteId`/`selectedCollectionId` are intentionally
// persisted — they are purely UI position state, scoped via the URL anyway,
// and outliving them across reloads is the desired UX.

interface WorkspaceState {
  currentWorkspace: Workspace | null;
  selectedDocId: string | null;
  selectedNoteId: string | null;
  selectedCollectionId: string | null;
  hydrated: boolean;

  setCurrentWorkspace: (workspace: Workspace | null) => void;
  /** Sync server-authoritative metadata (role, name, plan) without resetting
   *  selections or the cookie. Use for background refreshes where the workspace
   *  identity hasn't changed — only its properties have. */
  syncWorkspaceMetadata: (workspace: Workspace) => void;
  setSelectedDocId: (id: string | null) => void;
  setSelectedNoteId: (id: string | null) => void;
  setSelectedCollectionId: (id: string | null) => void;
  clearSelections: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set) => ({
      currentWorkspace: null,
      selectedDocId: null,
      selectedNoteId: null,
      selectedCollectionId: null,
      hydrated: false,

      setCurrentWorkspace: (workspace) => {
        // Mirror the selected workspace ID in a plain cookie so Server
        // Components can read it without going through localStorage.
        // The workspace ID is not sensitive (backend validates it against
        // the auth token), so HttpOnly is not needed here.
        if (typeof document !== 'undefined') {
          if (workspace) {
            document.cookie = `virza-ws-id=${workspace.id}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
          } else {
            document.cookie = 'virza-ws-id=; path=/; max-age=0';
          }
        }
        // Store metadata for sidebar display + clear stale selections.
        // NOTE: This setter is purely for metadata — the API client reads
        // workspace ID from the URL (not the store). Store resets are handled
        // explicitly by useWorkspaceTransition during workspace switches.
        set({
          currentWorkspace: workspace,
          selectedDocId: null,
          selectedNoteId: null,
          selectedCollectionId: null,
        });
      },

      syncWorkspaceMetadata: (workspace) => {
        // Update metadata (role, name, plan, etc.) from server without
        // resetting selections or updating the cookie. Only writes if
        // the workspace identity matches — prevents cross-workspace leaks.
        set((state) => {
          if (state.currentWorkspace?.id !== workspace.id) return state;
          return { currentWorkspace: workspace };
        });
      },

      setSelectedDocId: (id) => set({ selectedDocId: id, selectedNoteId: null }),

      setSelectedNoteId: (id) => set({ selectedNoteId: id, selectedDocId: null }),

      setSelectedCollectionId: (id) => set({ selectedCollectionId: id }),

      clearSelections: () =>
        set({
          selectedDocId: null,
          selectedNoteId: null,
          selectedCollectionId: null,
        }),
    }),
    {
      name: 'virza-workspace',
      // v5 bump: stop persisting `currentWorkspace`. Older clients with a
      // hydrated v4 blob will rehydrate without it (zustand's persist
      // middleware only restores keys it knows about from `partialize`).
      version: 5,
      // Migrate any stored version (0-4) to v5. All we carry forward are the
      // three selection IDs — everything else (including any stale workspace
      // blob from pre-v5 storage) is discarded. Providing this function
      // suppresses Zustand's "no migrate function was provided" console error.
      migrate: (persistedState) => {
        const s = (persistedState ?? {}) as Record<string, unknown>;
        return {
          selectedDocId: typeof s.selectedDocId === 'string' ? s.selectedDocId : null,
          selectedNoteId: typeof s.selectedNoteId === 'string' ? s.selectedNoteId : null,
          selectedCollectionId:
            typeof s.selectedCollectionId === 'string' ? s.selectedCollectionId : null,
        };
      },
      partialize: (state) => ({
        // currentWorkspace is intentionally OMITTED — see the SECURITY block
        // at the top of this file. It must come from the server on every
        // mount, never from localStorage.
        selectedDocId: state.selectedDocId,
        selectedNoteId: state.selectedNoteId,
        selectedCollectionId: state.selectedCollectionId,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Defensive: clear any legacy persisted workspace blob that
          // pre-dates v5 in case a client wrote it via a non-partialize
          // path. The server-confirmed setter is the only way to populate
          // this field.
          state.currentWorkspace = null;
          state.hydrated = true;
        }
      },
    },
  ),
);
